/**
 * Quantitative entity scoping — same rules as Risk Assessment EntityScoping.js:
 * 1) In-Scope if revenue or costs > planning materiality
 * 2) If coverage < 95%, add largest entities until >= 95%
 * 3) Final: In-Scope if either revenue or cost criteria met
 *
 * @param {Array<{ entity: string, revenue?: number, costs?: number }>} entities
 * @param {number} planningMateriality
 * @returns {Record<string, 'In-Scope' | 'Out-of-Scope'>}
 */
export function calculateQuantitativeScoping(entities, planningMateriality) {
  const list = Array.isArray(entities) ? entities : [];
  const pm = Number(planningMateriality) || 0;
  const totalRevenue = list.reduce((sum, e) => sum + (Number(e.revenue) || 0), 0);
  const totalCosts = list.reduce((sum, e) => sum + (Number(e.costs) || 0), 0);
  const conclusions = {};

  const revenueScoped = list.map((e) => ({
    ...e,
    inScopeByRevenue: (Number(e.revenue) || 0) > pm,
  }));

  let revenueInScopeTotal = revenueScoped
    .filter((e) => e.inScopeByRevenue)
    .reduce((sum, e) => sum + (Number(e.revenue) || 0), 0);

  let revenueCoverage = totalRevenue > 0 ? (revenueInScopeTotal / totalRevenue) * 100 : 0;

  if (revenueCoverage < 95) {
    const sortedByRevenue = [...revenueScoped]
      .filter((e) => !e.inScopeByRevenue)
      .sort((a, b) => (Number(b.revenue) || 0) - (Number(a.revenue) || 0));

    for (const entity of sortedByRevenue) {
      if (revenueCoverage >= 95) break;
      entity.inScopeByRevenue = true;
      revenueInScopeTotal += Number(entity.revenue) || 0;
      revenueCoverage = totalRevenue > 0 ? (revenueInScopeTotal / totalRevenue) * 100 : 0;
    }
  }

  const costScoped = revenueScoped.map((e) => ({
    ...e,
    inScopeByCost: (Number(e.costs) || 0) > pm,
  }));

  let costsInScopeTotal = costScoped
    .filter((e) => e.inScopeByCost)
    .reduce((sum, e) => sum + (Number(e.costs) || 0), 0);

  let costCoverage = totalCosts > 0 ? (costsInScopeTotal / totalCosts) * 100 : 0;

  if (costCoverage < 95) {
    const sortedByCosts = [...costScoped]
      .filter((e) => !e.inScopeByCost)
      .sort((a, b) => (Number(b.costs) || 0) - (Number(a.costs) || 0));

    for (const entity of sortedByCosts) {
      if (costCoverage >= 95) break;
      entity.inScopeByCost = true;
      costsInScopeTotal += Number(entity.costs) || 0;
      costCoverage = totalCosts > 0 ? (costsInScopeTotal / totalCosts) * 100 : 0;
    }
  }

  costScoped.forEach((entity) => {
    conclusions[entity.entity] =
      entity.inScopeByRevenue || entity.inScopeByCost ? 'In-Scope' : 'Out-of-Scope';
  });

  return conclusions;
}
